import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, Optional, PLATFORM_ID } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "@src/environments/environment";
import { Request } from "express";
import { REQUEST } from "src/express.tokens";

@Injectable({
  providedIn: "root",
})
/**
 * La clase `PlatformService` proporciona métodos para obtener información sobre la plataforma en la que se está ejecutando la aplicación.
 */
export class PlatformService {
  private _window: Window | undefined;
  private _document: Document | undefined;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document,
    @Optional() @Inject(REQUEST) private request: Request,
    private router: Router,
  ) {
    if (this.isBrowser()) {
      this._window = window;
      this._document = this.document;
    }
  }

  /**
   * @returns {boolean} - Devuelve `true` si la aplicación se está ejecutando en un navegador, de lo contrario, devuelve `false`.
   */
  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * @returns {string} - Devuelve el nombre del host de la ubicación actual.
   * - Si la aplicación se está ejecutando en un navegador, devuelve `window.location.hostname`.
   * - Si la aplicación se está ejecutando en un servidor, devuelve el host sacado de la petición.
   * - Si la aplicación se está ejecutando en un servidor y no se proporciona una petición, devuelve 'localhost', esto es útil para pruebas
   * ya que angular servido en desarrollo no tiene acceso a la petición.
   */
  getLocationHostname(): string {
    if (this.isBrowser()) {
      return this._window.location.hostname;
    } else {
      const hostname = this.request?.headers.host;
      if (!hostname) {
        if (typeof environment.isNgServe !== "undefined" && environment.isNgServe) {
          return "localhost";
        }
        throw new Error("No se pudo determinar el hostname en SSR");
      }
      return hostname;
    }
  }

  /**
   * @returns {string} - Devuelve el pathname de la ubicación actual.
   * - Si la aplicación se está ejecutando en un navegador, devuelve `window.location.pathname`.
   * - Si la aplicación se está ejecutando en un servidor, devuelve el pathname sacado de la petición.
   * - Si la aplicación se está ejecutando en un servidor y no se proporciona una petición, devuelve el pathname de la ruta actual.
   */
  getLocationPathname() {
    if (this.isBrowser()) {
      return this._window.location.pathname;
    } else if (this.document.location.pathname) {
      return this.document.location.pathname;
    } else {
      return "";
    }
  }

  /**
   * @returns {string} - Devuelve la URL de la ubicación actual.
   * - Si la aplicación se está ejecutando en un navegador, devuelve `window.location.href`.
   * - Si la aplicación se está ejecutando en un servidor, devuelve la URL sacada de la petición.
   * - Si la aplicación se está ejecutando en un servidor y no se proporciona una petición, devuelve la URL de la ruta actual.
   */
  getLocationUrl() {
    if (this.isBrowser()) {
      return this._window.location.href;
    } else if (this.request) {
      return this.request.protocol + "://" + this.request.get("host") + this.request.originalUrl;
    } else {
      return `http://localhost${this.router.url}`;
    }
  }

  safeDocumentOperation(callback: (doc: Document) => void) {
    if (this._document) {
      callback(this._document);
    }
  }

  safeWindowOperation(callback: (win: Window) => void) {
    if (this._window) {
      callback(this._window);
    }
  }
}
